import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
    ChevronLeft, ChevronRight, Calendar, Image, Video,
    Check, X, MessageSquare, Instagram, Facebook, 
    Youtube, Linkedin, Twitter, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Channel icons mapping
const channelIcons = {
    instagram: Instagram,
    facebook: Facebook,
    youtube: Youtube,
    linkedin: Linkedin,
    twitter: Twitter,
    tiktok: Globe,
    website: Globe
};

const channelColors = {
    instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
    facebook: 'bg-blue-600',
    youtube: 'bg-red-600',
    linkedin: 'bg-blue-700',
    twitter: 'bg-sky-500',
    tiktok: 'bg-black',
    website: 'bg-gray-600'
};

const statusConfig = {
    draft: { label: 'Entwurf', color: 'bg-gray-500', textColor: 'text-gray-500' },
    in_review: { label: 'In Prüfung', color: 'bg-yellow-500', textColor: 'text-yellow-600' },
    approved: { label: 'Freigegeben', color: 'bg-green-500', textColor: 'text-green-600' },
    revision_needed: { label: 'Überarbeitung', color: 'bg-orange-500', textColor: 'text-orange-600' },
    published: { label: 'Veröffentlicht', color: 'bg-blue-500', textColor: 'text-blue-600' }
};

const ContentPlanCalendar = ({ customerId, posts = [], isPublic = false, onRefresh }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedPost, setSelectedPost] = useState(null);
    const [feedbackText, setFeedbackText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Get days in month
    const daysInMonth = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];

        // Add empty days for padding at start
        const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
        for (let i = 0; i < startPadding; i++) {
            days.push({ date: null, posts: [] });
        }

        // Add actual days
        for (let d = 1; d <= lastDay.getDate(); d++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const dayPosts = posts.filter(p => p.date === dateStr);
            days.push({ 
                date: dateStr, 
                day: d, 
                posts: dayPosts,
                isToday: new Date().toISOString().split('T')[0] === dateStr
            });
        }

        return days;
    }, [currentDate, posts]);

    const monthName = currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

    const navigateMonth = (direction) => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + direction);
            return newDate;
        });
    };

    const handleFeedback = async (action) => {
        if (!selectedPost) return;
        
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/api/public/customer/${customerId}/content-plan/${selectedPost.id}/feedback`, {
                action,
                feedback: feedbackText
            });
            toast.success(action === 'approve' ? 'Beitrag freigegeben!' : 'Feedback gesendet!');
            setSelectedPost(null);
            setFeedbackText('');
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error('Fehler beim Senden des Feedbacks');
        } finally {
            setSubmitting(false);
        }
    };

    const PostCard = ({ post }) => {
        const ChannelIcon = channelIcons[post.channel] || Globe;
        const status = statusConfig[post.status] || statusConfig.draft;

        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedPost(post)}
                className="cursor-pointer"
            >
                <div className={`rounded-lg p-2 ${channelColors[post.channel] || 'bg-gray-500'} text-white shadow-sm hover:shadow-md transition-shadow`}>
                    <div className="flex items-center gap-1.5">
                        <ChannelIcon className="w-3 h-3" />
                        <span className="text-xs font-medium truncate">{post.caption?.substring(0, 20) || 'Kein Text'}</span>
                    </div>
                    {post.media_url && (
                        <div className="mt-1">
                            {post.media_type === 'video' ? (
                                <Video className="w-3 h-3 opacity-75" />
                            ) : (
                                <Image className="w-3 h-3 opacity-75" />
                            )}
                        </div>
                    )}
                </div>
            </motion.div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Month Navigation */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-2xl font-semibold dark:text-white">Content-Plan</h2>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-lg font-medium px-4 dark:text-white capitalize">{monthName}</span>
                    <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Calendar Grid */}
            <Card className="shadow-soft dark:bg-gray-800/50 dark:border-gray-700">
                <CardContent className="p-4">
                    {/* Weekday Headers */}
                    <div className="grid grid-cols-7 gap-2 mb-2">
                        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                            <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Days Grid */}
                    <div className="grid grid-cols-7 gap-2">
                        {daysInMonth.map((day, index) => (
                            <div 
                                key={index}
                                className={`min-h-[100px] p-2 rounded-lg border transition-colors ${
                                    day.date 
                                        ? day.isToday 
                                            ? 'border-primary bg-primary/5 dark:bg-primary/10' 
                                            : 'border-border/50 dark:border-gray-700 hover:bg-muted/30 dark:hover:bg-gray-700/30'
                                        : 'border-transparent bg-transparent'
                                }`}
                            >
                                {day.date && (
                                    <>
                                        <div className={`text-sm font-medium mb-2 ${
                                            day.isToday ? 'text-primary' : 'text-muted-foreground'
                                        }`}>
                                            {day.day}
                                        </div>
                                        <div className="space-y-1">
                                            {day.posts.slice(0, 3).map(post => (
                                                <PostCard key={post.id} post={post} />
                                            ))}
                                            {day.posts.length > 3 && (
                                                <div className="text-xs text-muted-foreground text-center">
                                                    +{day.posts.length - 3} mehr
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Status Legend */}
            <div className="flex flex-wrap items-center gap-4 justify-center">
                {Object.entries(statusConfig).map(([key, config]) => (
                    <div key={key} className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${config.color}`} />
                        <span className="text-sm text-muted-foreground">{config.label}</span>
                    </div>
                ))}
            </div>

            {/* Post Detail Modal */}
            <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
                <DialogContent className="max-w-2xl dark:bg-gray-900">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3">
                            {selectedPost && (
                                <>
                                    <div className={`w-8 h-8 rounded-lg ${channelColors[selectedPost.channel]} flex items-center justify-center`}>
                                        {React.createElement(channelIcons[selectedPost.channel] || Globe, { className: 'w-4 h-4 text-white' })}
                                    </div>
                                    <span className="capitalize">{selectedPost.channel}</span>
                                    <Badge className={statusConfig[selectedPost.status]?.color || 'bg-gray-500'}>
                                        {statusConfig[selectedPost.status]?.label || selectedPost.status}
                                    </Badge>
                                </>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    {selectedPost && (
                        <div className="space-y-4">
                            {/* Media Preview */}
                            {selectedPost.media_url && (
                                <div className="rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800">
                                    {selectedPost.media_type === 'video' ? (
                                        <video 
                                            src={selectedPost.media_url} 
                                            controls 
                                            className="w-full max-h-[300px] object-contain"
                                        />
                                    ) : (
                                        <img 
                                            src={selectedPost.media_url} 
                                            alt="Post media"
                                            className="w-full max-h-[300px] object-contain"
                                        />
                                    )}
                                </div>
                            )}

                            {/* Caption */}
                            <div>
                                <h4 className="text-sm font-medium text-muted-foreground mb-1">Beschreibung</h4>
                                <p className="text-sm dark:text-gray-300 whitespace-pre-wrap">
                                    {selectedPost.caption || 'Keine Beschreibung'}
                                </p>
                            </div>

                            {/* Hashtags */}
                            {selectedPost.hashtags?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Hashtags</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedPost.hashtags.map((tag, i) => (
                                            <Badge key={i} variant="outline" className="text-xs">
                                                #{tag}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Notes */}
                            {selectedPost.notes && (
                                <div>
                                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Notizen</h4>
                                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                        {selectedPost.notes}
                                    </p>
                                </div>
                            )}

                            {/* Previous Feedback */}
                            {selectedPost.customer_feedback && (
                                <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
                                    <h4 className="text-sm font-medium text-orange-700 dark:text-orange-400 mb-1 flex items-center gap-2">
                                        <MessageSquare className="w-4 h-4" />
                                        Vorheriges Feedback
                                    </h4>
                                    <p className="text-sm text-orange-600 dark:text-orange-300">
                                        {selectedPost.customer_feedback}
                                    </p>
                                </div>
                            )}

                            {/* Feedback Input (only for public and if status allows) */}
                            {isPublic && ['in_review', 'draft'].includes(selectedPost.status) && (
                                <div className="space-y-3 pt-4 border-t dark:border-gray-700">
                                    <h4 className="text-sm font-medium">Ihr Feedback</h4>
                                    <Textarea
                                        placeholder="Optionales Feedback oder Änderungswünsche..."
                                        value={feedbackText}
                                        onChange={(e) => setFeedbackText(e.target.value)}
                                        rows={3}
                                        className="dark:bg-gray-800"
                                    />
                                    <div className="flex gap-3">
                                        <Button
                                            onClick={() => handleFeedback('approve')}
                                            disabled={submitting}
                                            className="flex-1 bg-green-600 hover:bg-green-700"
                                        >
                                            <Check className="w-4 h-4 mr-2" />
                                            Freigeben
                                        </Button>
                                        <Button
                                            onClick={() => handleFeedback('request_revision')}
                                            disabled={submitting}
                                            variant="outline"
                                            className="flex-1 border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                                        >
                                            <X className="w-4 h-4 mr-2" />
                                            Überarbeitung anfordern
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Status indicator for approved/published */}
                            {['approved', 'published'].includes(selectedPost.status) && (
                                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-center">
                                    <Check className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto mb-1" />
                                    <p className="text-sm font-medium text-green-700 dark:text-green-400">
                                        Dieser Beitrag wurde freigegeben
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ContentPlanCalendar;
